import { Button } from '@sketch/components'
import styled, { css, DefaultTheme } from 'styled-components'

import { COMPARE_TINT } from '../../compareTint'
import { SPLIT_FRACTION_VAR } from '../../useSplitFraction'

/** Each its own control: a shared ground would read as a picker. */
export const Actions = styled.div`
  display: flex;
  align-items: center;

  /* Room for the focus ring on both sides: the footer clips what overflows it,
  and Safari draws its ring further out than other browsers */
  margin-block: 4px;

  /* The footer as a whole takes no pointer events, so these ask for them back. */
  pointer-events: auto;
`

/**
 * How a mode looks while it is the one being shown. The fill is the
 * caller's: a grey one would be too easy to miss on a canvas, so each says in
 * colour what the reader is looking at.
 */
const pressed = css`
  color: ${({ theme }) => theme.colors.foreground.primary.A};
  border: none;

  :hover {
    color: ${({ theme }) => theme.colors.foreground.primary.B};
  }
`

/**
 * Under a layer these sit over the design: the opaque fill and the border set
 * them apart from it.
 */
const Action = styled(Button).attrs({
  variant: 'secondary-untinted',
  size: '24',
})<{
  $active?: boolean
}>`
  /* Tighter than the scale goes: these sit on the canvas, not in a panel */
  padding: 3px 6px; /* stylelint-disable-line scales/space */
  min-height: auto;

  ${({ $active }) => $active && pressed}
`

/**
 * The whole entity is the other version while it is swapped, so the button
 * takes the colour that marks that version everywhere else - the outline of a
 * frame only it has, its caption, its row in the list.
 */
export const SwapButton = styled(Action)`
  ${({ $active }) =>
    $active &&
    css`
      background: ${COMPARE_TINT.strong};

      :hover {
        background: ${COMPARE_TINT.strong};
        color: ${({ theme }) => theme.colors.foreground.primary.B};
      }
    `}
`

/** The mark for the other version over the one on screen, at a ghost's strength. */
const ghostGround = (theme: DefaultTheme) =>
  `linear-gradient(${COMPARE_TINT.ghost}, ${COMPARE_TINT.ghost}) ${theme.colors.sketch.A}`

/** Both versions at once, so the button is both, one laid over the other. */
export const OverlayButton = styled(Action)`
  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${ghostGround(theme)};

      :hover {
        background: ${ghostGround(theme)};
        color: ${theme.colors.foreground.primary.B};
      }
    `}
`

export const SplitButton = styled(Action)<{ $sourceOnRight?: boolean }>`
  ${({ $active, $sourceOnRight, theme }) => {
    if (!$active) return undefined

    /*
    The two versions in the order the canvas has them: the compared one on
    whichever side it is drawn, so the button reads as a small picture of the
    overlay rather than a mirror of it.

    The line comes from the element's own inline style, so moving it changes a
    number rather than minting a class per position.
    */
    const line = `var(${SPLIT_FRACTION_VAR}, 50%)`
    const onScreen = theme.colors.sketch.A
    const compared = COMPARE_TINT.strong

    return css`
      background: ${$sourceOnRight
        ? `linear-gradient(to right, ${onScreen} 0 ${line}, ${compared} ${line})`
        : `linear-gradient(to right, ${compared} 0 ${line}, ${onScreen} ${line})`};
    `
  }}
`
